import { useEffect, useRef, useState } from "react";
import { Box, Card, Chip, CircularProgress, FormControl, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { Check, Cancel, Close } from "@mui/icons-material";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import {
  FlowTask,
  TaskPriority,
  TaskStatus,
  // TaskCategory,
} from "../../interfaces/flowTask.interface";
import {
  PRIORITY_COLORS,
  STATUS_COLORS,
  TASK_PRIORITIES,
  TASK_STATUSES,
  // CATEGORY_COLORS,
  // TASK_CATEGORIES,
} from "../../constants";
import { STATUS_LABELS } from "../../StatusLabels/statusLabels";
import { colors } from "../../../ui/colors";
import { useDialog } from "../../../ui/dialogs/useDialog";
import { useDeleteTodoTask } from "../../hooks/useDeleteTodoTask";
import { useUpdateTodoTask } from "../../hooks/useUpdateTodoTask";
import { useGlobalSnackbar } from "../../../../context/SnackbarContext";

// Fix z-index for Select menus to appear above BaseModalComponent
const getSelectMenuProps = () => ({
  disablePortal: false,
  container: () => {
    let container = document.getElementById("flow-do-select-container");
    if (!container) {
      container = document.createElement("div");
      container.id = "flow-do-select-container";
      container.style.zIndex = "99999999";
      container.style.position = "relative";
      document.body.appendChild(container);
    }
    return container;
  },
  PaperProps: {
    sx: {
      zIndex: "99999999 !important" as any,
      position: "fixed" as const,
    },
    style: {
      zIndex: 99999999,
      position: "fixed" as const,
    },
  } as any,
});

interface FlowTaskCardProps {
  task: FlowTask;
  isPreviewMode?: boolean;
  onClick?: () => void;
}

const Sx = {
  card: (borderColor: string, clickable: boolean, isUpdating: boolean) => ({
    p: 2,
    mb: 1.5,
    cursor: clickable ? "pointer" : "default",
    border: `1px solid ${borderColor}45`,
    borderRadius: 2,
    boxShadow: "none",
    transition: "all 0.2s ease",
    position: "relative",
    opacity: isUpdating ? 0.7 : 1,
    "&:hover": clickable
      ? {
          boxShadow: 2,
          transform: "translateY(-2px)",
        }
      : {},
  }),

  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.8)",
    borderRadius: 2,
    zIndex: 10,
  },

  deleteBtnWrap: {
    position: "absolute",
    bottom: 0,
    right: 0,
    zIndex: 1,
  },

  deleteBtn: {
    // backgroundColor: colors.gray[100],
    "&:hover": { backgroundColor: "transparent" },
    "&:hover .delete-icon": { color: colors.danger[500] },
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 1.5,
  },

  titleText: {
    fontSize: "0.95rem",
    fontWeight: 600,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    width: "100%",
  },

  titleInput: {
    mb: 1,
    "& .MuiOutlinedInput-root": { fontSize: "0.95rem", fontWeight: 600 },
  },

  notesDisplay: {
    mb: 1.5,
    minHeight: "2.5rem",
    cursor: "text",
    "&:hover": { bgcolor: "var(--fx-surface-muted, #F1F5F9)", borderRadius: 1 },
    px: 0.5,
    py: 0.25,
  },

  notesText: {
    fontSize: "0.8rem",
    color: colors.gray[700],

    overflow: "hidden",
    textOverflow: "ellipsis",

    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 3,

    whiteSpace: "normal",
    lineHeight: 1.4,
    maxHeight: "4.2em",

    wordBreak: "break-word",
  },

  notesPlaceholder: {
    color: "var(--fx-surface-texttertiary, #94A3B8)",
    fontSize: "0.8rem",
    fontStyle: "italic",
  },

  notesInput: {
    "& .MuiOutlinedInput-root": { fontSize: "0.8rem" },
  },

  notesActions: {
    display: "flex",
    gap: 0.5,
    mt: 0.5,
    justifyContent: "flex-end",
  },

  notesActionBtn: (bg: string, hoverBg: string, color: string) => ({
    bgcolor: bg,
    color,
    "&:hover": { bgcolor: hoverBg },
    width: 28,
    height: 28,
  }),

  footerRow: {
    display: "flex",
    justifyContent: "flex-start",
    gap: 1,
    alignItems: "center",
    mt: 1.5,
  },

  deadlineText: {
    color: "var(--fx-surface-texttertiary, #94A3B8)",
    fontSize: "0.7rem",
    cursor: "pointer",
    "&:hover": { color: "var(--fx-surface-textsecondary, #64748B)", bgcolor: "var(--fx-surface-muted, #F1F5F9)", borderRadius: 1 },
    px: 0.5,
    py: 0.25,
  },
};

const formatDeadline = (deadline?: string) => {
  if (!deadline) return null;
  try {
    return format(new Date(deadline), "MMM dd, yyyy", { locale: enUS });
  } catch {
    return null;
  }
};

export const FlowTaskCard = ({ task, isPreviewMode, onClick }: FlowTaskCardProps) => {
  const { showDialog, DialogComponent } = useDialog();
  const { showSnackbar } = useGlobalSnackbar();

  const deleteTaskMutation = useDeleteTodoTask();
  const updateTaskMutation = useUpdateTodoTask();

  const statusColor = STATUS_COLORS[task.status];
  const priorityColor = PRIORITY_COLORS[task.priority];

  const isUpdating = updateTaskMutation.isPending || deleteTaskMutation.isPending;

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isEditingDeadline, setIsEditingDeadline] = useState(false);

  const [titleValue, setTitleValue] = useState(task.name);
  const [notesValue, setNotesValue] = useState(task.description || "");
  const [deadlineValue, setDeadlineValue] = useState<Dayjs | null>(task.deadline ? dayjs(task.deadline) : null);

  const notesInputRef = useRef<HTMLInputElement>(null);
  const isSavingNotesRef = useRef(false);

  useEffect(() => {
    if (!isEditingTitle) setTitleValue(task.name);

    if (!isEditingNotes && !isSavingNotesRef.current) {
      const incomingNotes = task.description || "";
      if (notesValue !== incomingNotes) setNotesValue(incomingNotes);
    }
  }, [task.name, task.description, isEditingTitle, isEditingNotes]);

  const truncateText = (text: string, maxLength = 40) => {
    if (!text) return "";
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPreviewMode) return;

    showDialog({
      type: "confirm",
      title: "Delete Task",
      message: (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: colors.gray[700],
            }}
          >
            Are you sure you want to delete this task?
          </Typography>

          <Box
            title={task.name}
            sx={{
              px: 1.25,
              py: 1,
              borderRadius: 1.5,
              bgcolor: colors.gray[50],
              border: `1px solid ${colors.gray[200]}`,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: colors.gray[800],
                lineHeight: 1.35,

                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,

                overflow: "hidden",
                textOverflow: "ellipsis",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {task.name}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: "0.78rem",
              color: colors.gray[500],
            }}
          >
            This action cannot be undone.
          </Typography>
        </Box>
      ),
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        try {
          await deleteTaskMutation.mutateAsync({
            taskId: task.uid,
            kanbanCategoryUid: task.kanbanCategoryUid,
          });

          showSnackbar("Task deleted successfully!", "success", 5000, "bottom-left");
        } catch (error) {
          const msg = error instanceof Error ? error.message : "Failed to delete task. Please try again.";
          showSnackbar(msg, "error", 6000, "bottom-left");
        }
      },
      onCancel: () => {
        // no-op
      },
    });
  };

  const updateTask = (updates: Partial<FlowTask>) => {
    if (isPreviewMode) return;
    updateTaskMutation.mutate({ taskId: task.uid, updates });
  };

  const handleStatusChange = (newStatus: TaskStatus) => updateTask({ status: newStatus });
  const handlePriorityChange = (newPriority: TaskPriority) => updateTask({ priority: newPriority });

  const handleTitleBlur = () => {
    const next = titleValue.trim();

    if (next && next !== task.name) {
      updateTask({ name: next });
    } else if (!next) {
      setTitleValue(task.name);
    }

    setIsEditingTitle(false);
  };

  const handleNotesConfirm = () => {
    const trimmed = notesValue.trim();
    const current = task.description || "";

    if (trimmed === current || isPreviewMode) {
      setIsEditingNotes(false);
      return;
    }

    isSavingNotesRef.current = true;

    updateTaskMutation.mutate(
      {
        taskId: task.uid,
        updates: { description: trimmed || undefined },
      },
      {
        onSuccess: (updatedTask) => {
          const saved = updatedTask.description || "";
          setNotesValue(saved);
          setIsEditingNotes(false);

          setTimeout(() => {
            isSavingNotesRef.current = false;
          }, 100);
        },
        onError: () => {
          isSavingNotesRef.current = false;
        },
      },
    );
  };

  const handleNotesCancel = () => {
    setNotesValue(task.description || "");
    setIsEditingNotes(false);
  };

  const handleNotesBlur = (e: React.FocusEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (relatedTarget && (relatedTarget.closest(".notes-actions") || relatedTarget.closest("button"))) {
      return;
    }

    if (!isSavingNotesRef.current) handleNotesConfirm();
  };

  const handleDeadlineChange = (newValue: Dayjs | null) => {
    setDeadlineValue(newValue);
    updateTask({ deadline: newValue ? newValue.toISOString() : undefined });
    setIsEditingDeadline(false);
  };

  return (
    <>
      <Card onClick={onClick} sx={Sx.card(statusColor.border, Boolean(onClick), isUpdating)}>
        {isUpdating && (
          <Box sx={Sx.loadingOverlay}>
            <CircularProgress size={24} sx={{ color: colors.orange[500] }} />
          </Box>
        )}

        <Box sx={Sx.deleteBtnWrap}>
          <IconButton onClick={handleDeleteClick} disabled={isUpdating || isPreviewMode} sx={Sx.deleteBtn}>
            <Close className="delete-icon" sx={{ fontSize: 20, color: colors.gray[300] }} />
          </IconButton>
        </Box>

        {/* Header: Status + Priority */}
        <Box sx={Sx.headerRow} onClick={(e) => e.stopPropagation()}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={task.status}
              onChange={(e) => handleStatusChange(e.target.value as TaskStatus)}
              disabled={isUpdating || isPreviewMode}
              MenuProps={getSelectMenuProps()}
              sx={{
                height: 24,
                fontSize: "0.7rem",
                bgcolor: `${statusColor.color}10`,
                color: statusColor.color,
                fontWeight: 600,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                "& .MuiSvgIcon-root": { color: statusColor.color, fontSize: "1rem" },
                "& .MuiSelect-select": { color: statusColor.color },
              }}
            >
              {TASK_STATUSES.map((status) => {
                const sColor = STATUS_COLORS[status];
                return (
                  <MenuItem key={status} value={status}>
                    <Chip
                      label={STATUS_LABELS[status] ?? status.replace("_", " ")}
                      size="small"
                      sx={{
                        bgcolor: "transparent",
                        color: sColor.color,
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        height: 20,
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select
              value={task.priority}
              onChange={(e) => handlePriorityChange(e.target.value as TaskPriority)}
              disabled={isUpdating || isPreviewMode}
              MenuProps={getSelectMenuProps()}
              sx={{
                height: 20,
                fontSize: "0.65rem",
                bgcolor: priorityColor.bg,
                color: priorityColor.color,
                fontWeight: 600,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                "& .MuiSvgIcon-root": { color: priorityColor.color, fontSize: "0.9rem" },
              }}
            >
              {TASK_PRIORITIES.map((priority) => {
                const pColor = PRIORITY_COLORS[priority];
                return (
                  <MenuItem key={priority} value={priority}>
                    <Chip
                      label={priority}
                      size="small"
                      sx={{
                        bgcolor: "transparent",
                        color: pColor.color,
                        fontWeight: 600,
                        fontSize: "0.65rem",
                        height: 18,
                      }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

        {/* Title */}
        {isEditingTitle ? (
          <TextField
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") handleTitleBlur();
            }}
            autoFocus
            disabled={isUpdating || isPreviewMode}
            size="small"
            fullWidth
            sx={Sx.titleInput}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <Typography
            variant="body1"
            onClick={(e) => {
              e.stopPropagation();
              if (!isPreviewMode) setIsEditingTitle(true);
            }}
            sx={Sx.titleText}
          >
            {task.name}
          </Typography>
        )}

        {/* Notes */}
        {isEditingNotes ? (
          <Box sx={{ mb: 1.5 }} onClick={(e) => e.stopPropagation()}>
            <TextField
              inputRef={notesInputRef}
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              onBlur={handleNotesBlur}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") handleNotesCancel();
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleNotesConfirm();
                }
              }}
              disabled={isUpdating || isPreviewMode}
              multiline
              rows={2}
              size="small"
              fullWidth
              placeholder="Add notes..."
              sx={Sx.notesInput}
            />

            <Box className="notes-actions" sx={Sx.notesActions}>
              <IconButton
                size="small"
                onClick={handleNotesConfirm}
                disabled={isUpdating || isPreviewMode}
                sx={Sx.notesActionBtn(colors.success[50], colors.success[100], colors.success[700])}
              >
                <Check sx={{ fontSize: 18 }} />
              </IconButton>

              <IconButton
                size="small"
                onClick={handleNotesCancel}
                disabled={isUpdating || isPreviewMode}
                sx={Sx.notesActionBtn(colors.gray[100], colors.gray[200], colors.gray[700])}
              >
                <Cancel sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (!isPreviewMode) setIsEditingNotes(true);
            }}
            sx={Sx.notesDisplay}
          >
            {notesValue ? (
              <Tooltip
                title={notesValue}
                arrow
                placement="top"
                slotProps={{
                  popper: {
                    sx: {
                      zIndex: "99999999 !important",
                    },
                  },
                }}
              >
                <Typography variant="body2" sx={Sx.notesText}>
                  {notesValue}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="body2" sx={Sx.notesPlaceholder}>
                Click to add notes...
              </Typography>
            )}
          </Box>
        )}

        {/* Footer: Deadline */}
        <Box sx={Sx.footerRow} onClick={(e) => e.stopPropagation()}>
          {isEditingDeadline ? (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={deadlineValue}
                onChange={handleDeadlineChange}
                onClose={() => setIsEditingDeadline(false)}
                disabled={isUpdating || isPreviewMode}
                slotProps={{
                  textField: {
                    size: "small",
                    sx: {
                      width: 120,
                      "& .MuiInputBase-root": { fontSize: "0.7rem", height: 20 },
                    },
                  },
                  popper: {
                    sx: { zIndex: "99999999 !important" as any },
                  },
                }}
                format="MMM D, YYYY"
              />
            </LocalizationProvider>
          ) : (
            <Typography
              variant="caption"
              onClick={() => {
                if (!isPreviewMode) setIsEditingDeadline(true);
              }}
              sx={Sx.deadlineText}
            >
              {deadlineValue ? formatDeadline(deadlineValue.toISOString()) : "Set deadline"}
            </Typography>
          )}
        </Box>
      </Card>

      {DialogComponent}
    </>
  );
};
